import { useEffect, useRef, useState } from "react";
import { Loader2, Plus, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, errMsg, get } from "@/lib/api";
import { slug } from "@/lib/format";

const CLS = { index: "text-cyan-300", equity: "text-slate-300", future: "text-amber-300", option: "text-violet-300", other: "text-slate-500" };

export const SearchBar = ({ onOpen }) => {
  const [q, setQ] = useState("");
  const [ex, setEx] = useState("all");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const k = (e) => {
      if (e.key === "/" && document.activeElement?.tagName !== "INPUT" && document.activeElement?.tagName !== "TEXTAREA") {
        e.preventDefault();
        ref.current?.focus();
      }
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, []);

  useEffect(() => {
    if (!q.trim()) {
      setRes(null);
      return;
    }
    setBusy(true);
    const t = setTimeout(async () => {
      try {
        const d = await get("/instruments/search", { q, limit: 30, exchange: ex === "all" ? undefined : ex });
        setRes(d.results);
        setOpen(true);
      } catch (e) {
        toast.error(errMsg(e));
      } finally {
        setBusy(false);
      }
    }, 220);
    return () => clearTimeout(t);
  }, [q, ex]);

  const subscribe = async (r, e) => {
    e.stopPropagation();
    try {
      await api.post(`/symbols/${r.instrument_token}/subscribe`);
      toast.success(`Subscribed ${r.tradingsymbol}`);
      setRes((rs) => rs.map((x) => (x.instrument_token === r.instrument_token ? { ...x, subscribed: true } : x)));
    } catch (ex2) {
      toast.error(errMsg(ex2));
    }
  };

  return (
    <div className="relative" data-testid="search-control">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <Input
            ref={ref}
            data-testid="symbol-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onFocus={() => res && setOpen(true)}
            onKeyDown={(e) => e.key === "Escape" && setOpen(false)}
            placeholder="Search full instrument master — symbol, name, e.g. RELIANCE, NIFTY 24800 CE, BANKEX   ( / )"
            className="h-11 border-slate-800 bg-[#111827] pl-9 font-mono text-sm text-slate-100 placeholder:text-slate-600 focus-visible:ring-cyan-600"
          />
          {busy && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-slate-500" />}
        </div>
        <Select value={ex} onValueChange={setEx}>
          <SelectTrigger data-testid="search-exchange-select" className="h-11 w-[110px] border-slate-800 bg-[#111827] font-mono text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {["all", "NSE", "BSE", "NFO", "BFO", "MCX", "CDS"].map((x) => (
              <SelectItem key={x} value={x} data-testid={`search-exchange-${x}`}>{x === "all" ? "All exch" : x}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {open && res && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div data-testid="search-results" className="scroll-thin absolute left-0 right-0 z-40 mt-1 max-h-[420px] overflow-y-auto rounded-md border border-slate-700 bg-slate-900/95 shadow-2xl shadow-black/60 backdrop-blur-md">
            {res.length === 0 && <div className="px-4 py-6 text-center text-xs text-slate-500" data-testid="search-no-results">No instruments match “{q}”.</div>}
            {res.map((r) => (
              <div
                key={r.instrument_token}
                data-testid={`search-result-${slug(r.key)}`}
                onClick={() => { setOpen(false); onOpen(r); }}
                className="flex cursor-pointer items-center gap-3 border-b border-slate-800/70 px-3 py-2 transition-colors hover:bg-slate-800/70"
              >
                <span className={`w-14 font-mono text-[10px] uppercase ${CLS[r.cls]}`}>{r.cls}</span>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-mono text-sm text-slate-100">{r.tradingsymbol}</div>
                  <div className="truncate text-[11px] text-slate-500">
                    {r.name}{r.expiry ? ` · exp ${r.expiry}` : ""}{r.lot_size > 1 ? ` · lot ${r.lot_size}` : ""}
                  </div>
                </div>
                <span className="font-mono text-[10px] text-slate-500">{r.exchange}</span>
                {r.subscribed ? (
                  <span className="font-mono text-[10px] text-emerald-400">SUBSCRIBED</span>
                ) : (
                  <button data-testid={`search-subscribe-${slug(r.key)}`} onClick={(e) => subscribe(r, e)}
                    className="flex items-center gap-1 rounded border border-slate-700 px-2 py-0.5 font-mono text-[10px] text-slate-300 transition-colors hover:border-cyan-600 hover:text-cyan-300">
                    <Plus className="h-3 w-3" /> SUB
                  </button>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
